<template>
  <q-card
    flat
    :dark="isDarkModeGlobal"
  >
    <q-card-section class="q-gutter-xs q-pa-sm">

      <export-parameters-component/>

      <q-input
        :dark="isDarkModeGlobal"
        filled
        dense
        for="frequency_MHz"
        min="0.001"
        label="Fundamental Frequency [MHz]"
        type="number"
        step="0.001"
        v-model="frequency_MHz"
        :rules="rules"
        hide-bottom-space

        @update:modelValue="executeCalculation(frequency_MHz, 'pr')"
      />
      <q-dialog
        v-model="confirm"
        persistent
      >
        <q-card :dark="isDarkModeGlobal">
          <q-toolbar>
            <q-avatar
              icon="lock"
              color="primary"
              text-color="white"
            />

            <q-toolbar-title class="text-primary">Locked</q-toolbar-title>
          </q-toolbar>

          <q-card-section class="row items-center">
            <span class="q-ml-sm">{{ toggleMessage }}</span>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn
              flat
              label="Ok"
              color="primary"
              v-close-popup
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
      <div class="row q-gutter-xs">
        <q-select
          :dark="isDarkModeGlobal"
          dense
          v-model="selectedInputParameterOption"
          :options="options"
          label="Select a Metric"
          outlined
          emit-value
          map-options
          class="col"
        />

        <q-input
          v-if="selectedInputParameterOption == 'Pressure Amplitude'"
          :dark="isDarkModeGlobal"
          filled
          dense
          for="ppp_mpa"
          label="Free-Field Pressure Amplitude [MPa]"
          type="number"
          step="0.01"
          v-model="ppp_mpa"
          :rules="rules"
          hide-bottom-space
          @update:modelValue="executeCalculation(ppp_mpa, 'pr')"
          class="col"
        >
          <template #append>
            <ppp-pnp-docs />
          </template>
        </q-input>
        <q-input
          v-if="selectedInputParameterOption == 'Isppa'"
          :dark="isDarkModeGlobal"
          filled
          dense
          for="isppa"
          label="Free-Field Isppa [W/cm2]"
          type="number"
          step="0.01"
          min="0.001"
          v-model="isppa"
          :rules="rules"
          class="col"
          @update:modelValue="executeCalculation(isppa, 'isppa')"
          hide-bottom-space
        >
          <template #append>
            <reference-popup-proxy
              :icon="'help'"
              tooltip="Show note"
              :text="isppaMessage"
            />
          </template>
        </q-input>

        <q-input
          v-if="selectedInputParameterOption == 'Non-derated MI'"
          :dark="isDarkModeGlobal"
          filled
          dense
          for="non_derated_MI"
          label="Non-derated MI"
          type="number"
          step="0.01"
          v-model="non_derated_MI"
          :rules="rules"
          hide-bottom-space
          @update:modelValue="executeCalculation(non_derated_MI, 'non_derated_MI')"
          class="col"
        >
          <template #append>
            <reference-popup-proxy
              :icon="'help'"
              tooltip="Show note"
              :text="`The mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa. This estimate of MI is based on non-derated pressure and does not correspond to the formal definition of MI provided in the <a href='https://doi.org/10.1016/j.brs.2024.04.013' target='_blank'><strong>ITRUSST Standardised Reporting Consensus</strong></a>.`"
            />
          </template>
        </q-input>

        <q-input
          v-if="selectedInputParameterOption == 'MI'"
          :dark="isDarkModeGlobal"
          filled
          dense
          for="MI"
          label="MI"
          type="number"
          step="0.01"
          hint="derated by 0.3 dB/cm/MHz"
          v-model="MI"
          :rules="rules"
          @update:modelValue="executeCalculation(MI, 'MI')"
          class="col"
        >
          <template #append>
            <reference-popup-proxy
              :icon="'help'"
              tooltip="Show note"
              :text="`The mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa. This estimate of MI is based on derated pressure as defined in the <a href='https://doi.org/10.1016/j.brs.2024.04.013' target='_blank'><strong>ITRUSST Standardised Reporting Consensus</strong></a>. The pressure is derated using an attenuation coefficient of 0.3 dB/cm/MHz.`"
            />
          </template>
        </q-input>
      </div>

      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        filled
        dense
        for="diameter_of_transducer"
        label="Diameter of transducer [mm]"
        type="number"
        step="1"
        min="0"
        v-model="diameter_of_transducer_mm"
        :rules="rules"
        @update:modelValue="executeCalculation(diameter_of_transducer_mm, 'pr')"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="`Diameter measured at transducer exit plane.`"
          />
        </template>
      </q-input>
<q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        filled
        dense
        for="focal_depth"
        label="Focal depth [mm]"
        type="number"
        step="1"
        v-model="focal_depth_mm"
        :rules="[
          val => !!val || 'This field is required',
          val => val > 0 || 'Value must be > 0',
          val => val >= minimumValueFocalDepth || `Value must be >= ${minimumValueFocalDepth} (coupling thickness + scalp thickness + skull thickness)`
        ]"

        @update:modelValue="executeCalculation(focal_depth_mm, 'pr')"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="`Measured from transducer exit plane.`"
          />
        </template>
      </q-input>
    </q-card-section>
    <q-separator />

  </q-card>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {computed, ref} from "vue"
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"
import PppPnpDocs from "./DialogPeakPressureGraphDocs.vue"
import ExportParametersComponent from "./ExportParametersComponent.vue";

const calculatorStore = useCalculatorStore()
const {
  ppp_mpa,
  isppa,
  MI,
  diameter_of_transducer_mm,
  focal_depth_mm,
  thickness_of_skull_mm,
  thickness_of_scalp_mm,
  coupling_thickness_mm,
  frequency_MHz,
  isDarkModeGlobal,
  non_derated_MI,
  loadingValue
} = storeToRefs(calculatorStore)


const selectedInputParameterOption = ref("Pressure Amplitude")

const options = [
  { label: 'Pressure Amplitude', value: 'Pressure Amplitude' },
  { label: 'Isppa', value: 'Isppa' },
  { label: 'Non-derated MI', value: 'Non-derated MI' },
  { label: 'MI', value: 'MI' }
]

const deratingParameters = ref(true)
const confirm = ref(false)
const toggleMessage = 'Please see ITRUSST Standardised Reporting paper for information about situations in which PPP and PNP may differ. Currently, this calculator cannot account for all the effects of unequal PPP and PNP, and therefore the option to use unequal PPP and PNP is unavailable.'
const rules = [val => !!val || 'This field is required', val => val > 0 || 'Value must be > 0']
const minimumValueFocalDepth = computed(() => +coupling_thickness_mm.value + +thickness_of_scalp_mm.value + +thickness_of_skull_mm.value)
const isppaMessage = `Spatial-peak pulse average intensity; the Isppa is calculated for an individual pulse taking pulse level ramping into account. However, when ramping is applied at the pulse train or pulse train repetition level, the pulse amplitude and therefore Isspa is not consistent over time. The Isppa reported here is the maximum value which corresponds to the highest amplitude pulses, which occur after the on-ramp of the pulse, pulse train, and/or pulse train repetition level is completed, and before the off-ramp begins.`

const blurActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    activeElement.blur()
  }
};

let debounceTimeout = null // Timeout reference for debounce
let interval = null // Timeout reference for interval
const loading = ref(false) // Tracks spinner visibility

let totalTime = 3000
let intervalTime = 100 // Time between each count

function executeCalculation(val: number, calcType: string) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    clearInterval(interval)
    loadingValue.value = 0
  }

  loading.value = true;
  interval = setInterval(() => {
    loadingValue.value += 0.1;
    if (loadingValue.value > 3) {
      clearInterval(interval); // Stop the interval when count reaches 100
      loadingValue.value = 0
    }
  }, intervalTime);
      // Start debounce timer
  debounceTimeout = setTimeout(() => {
        // After debounce delay
    loading.value = false;
    if (val > 0 && focal_depth_mm.value >= minimumValueFocalDepth.value) {
      switch (calcType) {
        case 'acoustic_power':
          calculatorStore.updateByAcousticPower()
          break
        case 'transducer_efficiency':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'electrical_power':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'pr':
          calculatorStore.updateByPr()
          break
        case 'MI':
          calculatorStore.updateByMI()
          break
        case 'non_derated_MI':
          calculatorStore.updateByNonDeratedMI()
          break
        default:
          calculatorStore.updateByIsppa()
          break
      }
      calculatorStore.clearNotifications()

      blurActiveInput()
    } else {
      console.error("empty value or 0 not allowed")
    }
  }, totalTime);
}
</script>
